import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Sparkle, Sparkles, Zap, type LucideIcon } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import {
  discountedUsd,
  getPlanFeaturesForPlan,
  getPlanTerms,
  type LineupPlanId,
  type PricingEdition,
} from '@highlight/common'
import type { BillingDiscount, BillingPlanV2Tier } from '@highlight/common/types'
import { formatShortDate } from '@renderer/chat/utils/billingUtils'
import './plan-select-cards.css'

/**
 * The v2 tiers a user can pick from the selector. An alias, not a copy: spelling
 * the union out again let a new tier reach billing-shared while every
 * `Record<SelectableV2Plan, …>` in the UI stayed silently stale.
 */
export type SelectableV2Plan = BillingPlanV2Tier

/** Theme tokens any tier-tinted surface may resolve to, including the alert states. */
export type PlanThemeToken = 'brand' | 'premium-green' | 'premium-blue' | 'warning' | 'red' | 'foreground-secondary'

/** Tokens that alias a Prism ramp step and so already hold a full colour; the rest are bare HSL channels. */
const FULL_COLOR_TOKENS: ReadonlySet<PlanThemeToken> = new Set(['brand', 'warning', 'foreground-secondary'])

/** A plan token as a CSS colour at the given alpha, in whichever value format the token uses. */
export function planTokenColor(token: PlanThemeToken, alpha?: number): string {
  if (FULL_COLOR_TOKENS.has(token)) {
    return alpha === undefined ? `var(--${token})` : `oklch(from var(--${token}) l c h / ${alpha})`
  }
  return alpha === undefined ? `hsl(var(--${token}))` : `hsl(var(--${token}) / ${alpha})`
}

interface PlanTheme {
  /** Theme token behind the card wash and page tint. */
  token: Extract<PlanThemeToken, 'brand' | 'premium-green' | 'premium-blue'>
  /** Dot-grid texture inside the wash; the Figma tiers use different frequencies. */
  dots: { radiusPx: number; gridPx: number }
  /** Solid accent for small tier indicators (radio dot, stepper fill). */
  accentBg: string
  /**
   * Solid accent for the flow's primary CTA while this plan is selected. Pins its
   * own label colour in both themes: the onboarding CTA is Button's `bold`
   * variant, whose `dark:text-black/90` is right for brand yellow and wrong for
   * every tier fill laid over it.
   */
  continueButton: string
  /** Same CTA while disabled, kept in the tier's hue. */
  continueDisabled: string
}

// Static class strings so Tailwind's scanner sees every variant.
const PLAN_THEMES: Record<SelectableV2Plan, PlanTheme> = {
  lite: {
    token: 'brand',
    dots: { radiusPx: 3, gridPx: 28 },
    accentBg: 'bg-brand',
    continueButton: 'bg-brand text-black hover:bg-brand/90 hover:text-black dark:text-black dark:hover:text-black',
    continueDisabled: 'disabled:bg-brand/30 disabled:text-foreground-placeholder',
  },
  pro2: {
    token: 'premium-green',
    dots: { radiusPx: 1.25, gridPx: 10 },
    accentBg: 'bg-premium-green',
    continueButton:
      'bg-premium-green text-white hover:bg-premium-green/90 hover:text-white dark:text-white dark:hover:text-white',
    continueDisabled: 'disabled:bg-premium-green/25 disabled:text-premium-green',
  },
  max: {
    token: 'premium-blue',
    dots: { radiusPx: 1.5, gridPx: 12 },
    accentBg: 'bg-premium-blue',
    continueButton:
      'bg-premium-blue text-white hover:bg-premium-blue/90 hover:text-white dark:text-white dark:hover:text-white',
    continueDisabled: 'disabled:bg-premium-blue/25 disabled:text-premium-blue',
  },
}

/** Free uses Lite's colours because the revamp lists it in Lite's place. */
function planThemeFor(plan: LineupPlanId): PlanTheme {
  return PLAN_THEMES[plan === 'free' ? 'lite' : plan]
}

/** CTA accent classes for the currently selected plan (Continue/Upgrade buttons). */
export function planContinueClasses(plan: LineupPlanId): string {
  return planThemeFor(plan).continueButton
}

/**
 * Disabled CTA for the selected plan. The onboarding button's own disabled
 * yellow reads as a broken button once the page carries a green or blue tint.
 */
export function planDisabledClasses(plan: SelectableV2Plan): string {
  return PLAN_THEMES[plan].continueDisabled
}

/** Solid tier accent, e.g. the onboarding stepper fill while a plan is selected. */
export function planAccentClasses(plan: SelectableV2Plan): string {
  return PLAN_THEMES[plan].accentBg
}

/** The tier's theme token, for surfaces composing their own gradients from it. */
export function planToken(plan: LineupPlanId): PlanTheme['token'] {
  return planThemeFor(plan).token
}

/**
 * Button's `secondary` base — what every solid CTA here is laid over — lightens
 * its border on hover, which over a filled button reads as a stray ring rather
 * than as the button lifting. The fills hold the border flat instead.
 */
export const SOLID_CTA_BORDER_CLASSES = 'border-transparent hover:border-transparent'

/**
 * The solid dark CTA, for the action a surface is pushing hardest. Lives here
 * beside the tier accents so the two treatments stay one decision.
 */
export const SOLID_DARK_CTA_CLASSES = `bg-foreground text-background hover:bg-foreground/90 hover:text-background ${SOLID_CTA_BORDER_CLASSES}`

/** The tier's dot-lattice frequency, for surfaces rendering their own TwinkleDots band. */
export function planDots(plan: LineupPlanId): PlanTheme['dots'] {
  return planThemeFor(plan).dots
}

/** Tier gradient for the brand-mark tile at the head of the subscription dialog. */
export function planLogoStyle(plan: SelectableV2Plan): CSSProperties {
  const { token } = PLAN_THEMES[plan]
  return {
    // The gradient opens at a fifth of the tier colour, so it needs something
    // opaque beneath it. Without this the tile's pale corner is a window onto
    // the dialog's own wash and dot grid, which is not what a solid brand tile
    // should show through.
    backgroundColor: 'var(--background)',
    backgroundImage: `linear-gradient(206.57deg, ${planTokenColor(token, 0.2)} 9%, ${planTokenColor(token)} 92%)`,
  }
}

/**
 * Tier order, cheapest first. Keyed off PLAN_THEMES rather than written out
 * again: a tier added to the union has to gain a theme entry to compile, and
 * gets its place here for free. Insertion order is the price order.
 */
export const TIER_ORDER: ReadonlyArray<SelectableV2Plan> = Object.keys(PLAN_THEMES) as Array<SelectableV2Plan>

/**
 * The tier one step up, for upgrade CTAs; the top tier has none. Free steps up
 * to Pro, because the revamp, the only lineup that sells Free, has no Lite.
 */
export function nextTierAbove(plan: LineupPlanId): SelectableV2Plan | null {
  if (plan === 'free') {
    return 'pro2'
  }
  return TIER_ORDER[TIER_ORDER.indexOf(plan) + 1] ?? null
}

/** Every plan a switch can land on, cheapest first, with Free below the tiers. */
const PLAN_LADDER: ReadonlyArray<LineupPlanId> = ['free', ...TIER_ORDER]

/** Whether switching to `target` is a step up from the plan in force. */
export function isUpgradeFrom(target: LineupPlanId, current: LineupPlanId | null): boolean {
  return current === null || PLAN_LADDER.indexOf(target) > PLAN_LADDER.indexOf(current)
}

// Gradient backgrounds are inline styles (BrandWash precedent): background-image
// can't interpolate, so a surface that changes tier crossfades opacity on
// always-mounted layers instead. Quieter in dark mode, where a saturated wash
// behind light text costs contrast.
const washStyle = (token: PlanTheme['token'], isDark: boolean): CSSProperties => ({
  background: isDark
    ? `linear-gradient(180deg, ${planTokenColor(token, 0.45)} 0%, ${planTokenColor(token, 0.16)} 55%, transparent 100%)`
    : `linear-gradient(180deg, ${planTokenColor(token, 0.9)} 0%, ${planTokenColor(token, 0.35)} 55%, transparent 100%)`,
})

// Choreography of the dot grid: showing it twinkles the dots in — each dot
// fades up at its own random moment across the spread window, and a random
// quarter flip in instead (all in by ~550ms). Hiding twinkles them back out
// over ~420ms.
const DOTS_BAND_HEIGHT_PX = 96
const DOTS_FADE_SPREAD_MS = 350
const DOT_FADE_IN_MS = 200
const DOT_FLIP_IN_MS = 400
const DOTS_FLIP_FRACTION = 0.85
const DOTS_FADE_OUT_SPREAD_MS = 250
const DOT_VANISH_MS = 170

// Deterministic per-dot randomness (the classic GLSL sine hash), stable across
// renders so showing the grid again replays the same pattern.
const hash01 = (x: number, y: number, seedX: number, seedY: number): number => {
  const raw = Math.sin(x * seedX + y * seedY) * 43758.5453
  return raw - Math.floor(raw)
}

// Vertical fade so the dots dissolve downward with the wash gradient.
const dotsFadeMask: CSSProperties = {
  maskImage: 'linear-gradient(180deg, black 0%, transparent 85%)',
  WebkitMaskImage: 'linear-gradient(180deg, black 0%, transparent 85%)',
}

// Dots run quieter in dark mode so they don't fight the text for contrast.
const dotAlphaFor = (isDark: boolean): number => (isDark ? 0.18 : 0.55)

/** The tier wash gradient for tier-tinted surfaces outside the cards (e.g. the meeting-hours paywall header). */
export function planWashStyle(plan: LineupPlanId, isDark: boolean): CSSProperties {
  return washStyle(planThemeFor(plan).token, isDark)
}

/**
 * Static dot-grid texture matching the tier's wash, for placements that don't
 * animate selection. Mirrors TwinkleDots' resting frame.
 */
export function planWashDotsStyle(plan: SelectableV2Plan, isDark: boolean): CSSProperties {
  const { dots } = PLAN_THEMES[plan]
  return {
    backgroundImage: `radial-gradient(circle, hsl(0 0% 100% / ${dotAlphaFor(isDark)}) ${dots.radiusPx}px, transparent ${dots.radiusPx + 0.5}px)`,
    backgroundSize: `${dots.gridPx}px ${dots.gridPx}px`,
    backgroundPosition: `${dots.gridPx / 2}px ${dots.gridPx / 2}px`,
    ...dotsFadeMask,
  }
}

export interface TwinkleDotsProps {
  dots: PlanTheme['dots']
  isDark: boolean
  shown: boolean
  /** Band the lattice fills. Must match the height of the box this is placed in. */
  heightPx?: number
}

/**
 * The wash's dot grid as real elements so each dot can animate on its own
 * clock: a randomized twinkle-in, with a random quarter flipping in around
 * their vertical axis. Frequency comes from the theme (the Figma tiers
 * differ), and the dots run quieter in dark mode so they don't fight the text
 * for contrast.
 */
export function TwinkleDots({ dots, isDark, shown, heightPx = DOTS_BAND_HEIGHT_PX }: TwinkleDotsProps) {
  const bandRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  // Dots mount on first selection: the out animation's 0% frame shows the
  // dot, so running it on a never-selected card would flash the grid — and
  // until then the spans have nothing to do anyway.
  const [everShown, setEverShown] = useState(false)
  if (shown && !everShown) setEverShown(true)

  // When each entrance began, so an interrupted one can tell which dots were
  // still waiting on their in-delay: those must skip the out animation too
  // (same full-opacity 0% frame), or rapid plan switching flashes them in.
  const shownAtRef = useRef(0)
  useEffect(() => {
    if (shown) shownAtRef.current = performance.now()
  }, [shown])
  // Monotonic per deselect, so a dot's classification never flips mid-fade.
  const shownForMs = !shown && everShown ? performance.now() - shownAtRef.current : Infinity

  // Cards share the grid's fluid columns, so the dot lattice is laid out from
  // the measured band width and follows resizes.
  useLayoutEffect(() => {
    const band = bandRef.current
    if (!band) return
    const observer = new ResizeObserver(() => setWidth(band.clientWidth))
    observer.observe(band)
    setWidth(band.clientWidth)
    return () => observer.disconnect()
  }, [])

  const dotPositions = useMemo(() => {
    if (!width) return []
    const { gridPx } = dots
    const cols = Math.ceil(width / gridPx)
    const rows = Math.ceil(heightPx / gridPx)
    const xInset = (width - cols * gridPx) / 2
    const positions: Array<{ x: number; y: number; delayFrac: number; flips: boolean }> = []
    for (let col = 0; col < cols; col++) {
      for (let row = 0; row < rows; row++) {
        const x = xInset + gridPx / 2 + col * gridPx
        const y = gridPx / 2 + row * gridPx
        const delayFrac = hash01(x, y, 12.9898, 78.233)
        const flips = hash01(x, y, 39.3467, 11.135) < DOTS_FLIP_FRACTION
        positions.push({ x, y, delayFrac, flips })
      }
    }
    return positions
  }, [dots, width, heightPx])

  const dotAlpha = dotAlphaFor(isDark)
  return (
    <div ref={bandRef} className="absolute inset-0" style={dotsFadeMask}>
      {everShown &&
        dotPositions.map(({ x, y, delayFrac, flips }) => {
          const startedEntering = delayFrac * DOTS_FADE_SPREAD_MS <= shownForMs
          return (
            <span
              key={`${x},${y}`}
              className="absolute rounded-full"
              style={{
                left: x - dots.radiusPx,
                top: y - dots.radiusPx,
                width: dots.radiusPx * 2,
                height: dots.radiusPx * 2,
                backgroundColor: `hsl(0 0% 100% / ${dotAlpha})`,
                // Base opacity matches each animation's resting frame; fill
                // 'both' covers the random delays on either side.
                opacity: shown ? 1 : 0,
                animation: shown
                  ? flips
                    ? `plan-dots-flip-in ${DOT_FLIP_IN_MS}ms ease-out both`
                    : `plan-dots-fade-in ${DOT_FADE_IN_MS}ms ease-out both`
                  : startedEntering
                    ? `plan-dots-fade-out ${DOT_VANISH_MS}ms ease-in both`
                    : // Never appeared, nothing to fade — rest at base opacity 0.
                      undefined,
                animationDelay: shown
                  ? `${delayFrac * DOTS_FADE_SPREAD_MS}ms`
                  : `${delayFrac * DOTS_FADE_OUT_SPREAD_MS}ms`,
              }}
            />
          )
        })}
    </div>
  )
}

// Allowances come from the billing constants, so a feature list can't promise
// hours or credits the plan doesn't grant.
function transcriptionHours(plan: SelectableV2Plan, edition: PricingEdition): string {
  const hours = getPlanTerms(plan, edition).monthlyMeetingHours
  return hours === null ? 'Unlimited meeting transcription' : `${hours} hours of meeting transcription`
}

function monthlyCredits(plan: SelectableV2Plan, edition: PricingEdition, spentOn: string): string {
  return `${getPlanTerms(plan, edition).monthlyCredits.toLocaleString('en-US')} credits for ${spentOn}`
}

// The subscription dialog lists what the tier itself holds, so no
// "Everything in ..." shorthand: there is no cheaper card next to it to
// resolve the reference.
function subscriptionFeaturesFor(edition: PricingEdition): Record<SelectableV2Plan, Array<string>> {
  const proSkills = getPlanFeaturesForPlan('pro2', edition).createCustomSkills
    ? 'Built-in & custom skills'
    : 'Built-in skills'
  return {
    lite: [
      transcriptionHours('lite', edition),
      'Pre-meeting briefs',
      'Meeting summaries',
      'Reminders',
      'Chat',
      monthlyCredits('lite', edition, 'chat'),
    ],
    pro2: [
      transcriptionHours('pro2', edition),
      'Pre-meeting briefs',
      'Meeting summaries',
      'Chat',
      'Reminders',
      proSkills,
      monthlyCredits('pro2', edition, 'chat & skills'),
    ],
    max: [
      transcriptionHours('max', edition),
      'Pre-meeting briefs',
      'Meeting summaries',
      'Chat',
      'Reminders',
      'Built-in & custom skills',
      monthlyCredits('max', edition, 'chat & skills'),
      'White glove support',
    ],
  }
}

function meetingBriefs(plan: SelectableV2Plan, edition: PricingEdition): string {
  const briefs = getPlanTerms(plan, edition).monthlyMeetingBriefs
  return briefs === null ? 'Automatic pre-meeting briefs' : `${briefs} pre-meeting briefs on request`
}

function skills(plan: SelectableV2Plan, edition: PricingEdition): string {
  return getPlanFeaturesForPlan(plan, edition).createCustomSkills ? 'Built-in & custom skills' : 'Built-in skills'
}

/** The revamp's lists follow the plan comparison row for row, so the dialog and the table agree. */
function revampSubscriptionFeatures(): Record<SelectableV2Plan, Array<string>> {
  const paidTier = (plan: SelectableV2Plan) => [
    transcriptionHours(plan, 'v2-revamp'),
    meetingBriefs(plan, 'v2-revamp'),
    'Post-meeting summaries',
    'Daily briefs',
    'Daily recaps',
    'Reminders',
    skills(plan, 'v2-revamp'),
    monthlyCredits(plan, 'v2-revamp', 'chat & skills'),
  ]
  return {
    // Lite is off the revamp's lineup but keeps its subscribers.
    lite: subscriptionFeaturesFor('v2-revamp').lite,
    pro2: paidTier('pro2'),
    max: [...paidTier('max'), 'Access to beta features', 'White glove support'],
  }
}

const SUBSCRIPTION_FEATURES_BY_EDITION: Record<PricingEdition, Record<SelectableV2Plan, Array<string>>> = {
  v2: subscriptionFeaturesFor('v2'),
  'v2-revamp': revampSubscriptionFeatures(),
}

/** The tier's feature list as the subscription dialog states it. */
export function planSubscriptionFeatureList(plan: SelectableV2Plan, edition: PricingEdition): Array<string> {
  return SUBSCRIPTION_FEATURES_BY_EDITION[edition][plan]
}

/** Short tier label ('Free'/'Lite'/'Pro'/'Max') shared by every tier-card surface. */
export function planTierLabel(plan: LineupPlanId): string {
  // Labels are the same on every edition.
  return getPlanTerms(plan, 'v2').label
}

/**
 * Each tier's mark. They differ in Figma, so the badge tells the tiers apart
 * before the word is read.
 */
const PLAN_MARKS: Record<LineupPlanId, LucideIcon> = {
  free: Sparkle,
  lite: Sparkle,
  pro2: Sparkles,
  max: Zap,
}

const planBadgeVariants = cva(
  'bg-background text-foreground inline-flex shrink-0 items-center gap-2 rounded-full text-[16px] font-semibold shadow-md',
  {
    variants: {
      size: {
        default: 'px-2 py-1',
        /** The Subscription hero, where the badge sits beside a 24px heading. */
        large: 'px-2.5 py-1.5',
      },
    },
    defaultVariants: { size: 'default' },
  },
)

export interface PlanBadgeProps extends VariantProps<typeof planBadgeVariants> {
  plan: LineupPlanId
  className?: string
}

/**
 * The tier pill: the tier's mark and its name, on the onboarding plan cards,
 * the plan comparison columns and the Subscription hero. Lives here beside the
 * rest of the tier vocabulary so a tier can't wear one mark on one screen and
 * a different one on the next.
 */
export function PlanBadge({ plan, size, className }: PlanBadgeProps) {
  const Mark = PLAN_MARKS[plan]
  return (
    <span className={twMerge(planBadgeVariants({ size }), className)}>
      <Mark className="size-4 shrink-0" />
      {planTierLabel(plan).toUpperCase()}
    </span>
  )
}

/** What the pricing surfaces need of a discount grant. */
export type PlanDiscount = Pick<BillingDiscount, 'percentOff' | 'durationMonths' | 'endsAt'>

/** A dollar price the way the cards print it: whole dollars bare, cents only when a discount leaves some. */
export function formatPriceUsd(usd: number): string {
  return Number.isInteger(usd) ? `${usd}` : usd.toFixed(2)
}

export function planMonthlyPriceUsd(
  plan: LineupPlanId,
  edition: PricingEdition,
  discount?: PlanDiscount | null,
): number {
  const listUsd = getPlanTerms(plan, edition).monthlyPriceUsd
  return discount ? discountedUsd(listUsd, discount.percentOff) : listUsd
}

export function planDiscountNote(plan: LineupPlanId, edition: PricingEdition, discount: PlanDiscount): string {
  const span = discount.endsAt ? `until ${formatShortDate(discount.endsAt)}` : `for ${discount.durationMonths} months`
  return `${discount.percentOff}% off ${span}, then $${getPlanTerms(plan, edition).monthlyPriceUsd}`
}

const PLAN_PRICE_SIZE = {
  large: 'text-foreground text-[28px] font-semibold leading-none',
  medium: 'text-foreground text-[22px] font-medium leading-none',
  paywall: 'text-foreground text-[24px] font-medium leading-5',
  small: 'text-foreground-secondary text-[15px] font-semibold',
} as const

export interface PlanPriceProps {
  plan: LineupPlanId
  edition: PricingEdition
  discount?: PlanDiscount | null
  size: keyof typeof PLAN_PRICE_SIZE
  className?: string
}

/**
 * Discounted, the price the account pays leads and the list price sits struck through beside it,
 * so every surface prints the pair the same way.
 */
export function PlanPrice({ plan, edition, discount, size, className }: PlanPriceProps) {
  const listUsd = getPlanTerms(plan, edition).monthlyPriceUsd
  if (plan === 'free') {
    return <span className={twMerge(PLAN_PRICE_SIZE[size], className)}>Free</span>
  }
  if (!discount) {
    return <span className={twMerge(PLAN_PRICE_SIZE[size], className)}>${listUsd}</span>
  }
  return (
    <span className={twMerge('inline-flex items-baseline gap-1.5', className)}>
      <span className={PLAN_PRICE_SIZE[size]}>${formatPriceUsd(planMonthlyPriceUsd(plan, edition, discount))}</span>
      <s className="text-foreground-placeholder text-[15px] font-normal">${listUsd}</s>
    </span>
  )
}
